<template>
    <list-container fixed>
        <template #header>
            <list-cell-header :width="20">
                <template #title>
                    <lead-list-sort-decorator
                        :selected-sort="selectedSort"
                        column-name="primaryContact.name.firstName"
                        @sort="emit('sort', $event)"
                    >
                        Naam
                    </lead-list-sort-decorator>
                </template>
            </list-cell-header>
            <list-cell-header :width="20">
                <template #title>
                    <lead-list-sort-decorator
                        :selected-sort="selectedSort"
                        column-name="primaryContact.email"
                        @sort="emit('sort', $event)"
                    >
                        E-mailadres
                    </lead-list-sort-decorator>
                </template>
            </list-cell-header>
            <list-cell-header :width="20">
                <template #title>
                    <lead-list-sort-decorator
                        :selected-sort="selectedSort"
                        column-name="primaryContact.address.street"
                        @sort="emit('sort', $event)"
                    >
                        Adres
                    </lead-list-sort-decorator>
                </template>
            </list-cell-header>
            <list-cell-header :width="20">
                <template #title>
                    <lead-list-sort-decorator
                        :selected-sort="selectedSort"
                        column-name="pipeline.name"
                        @sort="emit('sort', $event)"
                    >
                        <slot name="flowFilter" />
                    </lead-list-sort-decorator>
                </template>
            </list-cell-header>
            <list-cell-header :width="20">
                <template #title>
                    <lead-list-sort-decorator
                        :selected-sort="selectedSort"
                        column-name="createdAt"
                        default-direction="DESC"
                        @sort="emit('sort', $event)"
                    >
                        Aangemaakt
                    </lead-list-sort-decorator>
                </template>
            </list-cell-header>
            <list-cell-header :width="1" />
        </template>
        <template #content>
            <template v-if="props.leadCollection.data.value.length > 0 && !props.leadCollection.loading.value">
                <list-row
                    v-for="(lead, index) in props.leadCollection.data.value"
                    :key="lead.id.value"
                    :background-color="index % 2 ? 'gray-100' : 'white'"
                    @mouseenter="setCurrentLeadId(lead.id)"
                    @focusin="setCurrentLeadId(lead.id)"
                    @mouseleave="resetCurrentLeadId()"
                    @focusout="resetCurrentLeadId()"
                    @click="toDetail(lead)"
                >
                    <list-cell-text>
                        <template #content>
                            {{ lead.primaryContact.name.fullName }}
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            <template v-if="lead.primaryContact.email">
                                {{ lead.primaryContact.email.value.toLowerCase() }}
                            </template>
                            <template v-else>
                                <chip-informative :color="'gray-500'">
                                    Email onbekend
                                </chip-informative>
                            </template>
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            <template v-if="hasAddress(lead.primaryContact.address)">
                                {{ lead.primaryContact.address.fullStreet }}<br />
                                {{ lead.primaryContact.address.city }} {{ lead.primaryContact.address.postalCode }}
                            </template>
                            <template v-else>
                                <chip-informative :color="'gray-500'">
                                    Adres onbekend
                                </chip-informative>
                            </template>
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            {{ lead.pipeline.name }}
                        </template>
                    </list-cell-text>
                    <list-cell-text>
                        <template #content>
                            {{ formatDate(lead.createdAt) }}
                        </template>
                    </list-cell-text>
                    <list-cell>
                        <flex-container
                            v-show="lead.id.equals(currentLeadId)"
                            height="full"
                            align-content="center"
                            justify-content="end"
                            style="padding-right: 10px"
                        >
                            <button-default>
                                <svg-icon
                                    :icon="MoreVertIcon"
                                    :size="IconSize.md"
                                    :color="'primary'"
                                    is-custom-icon
                                />
                            </button-default>
                        </flex-container>
                    </list-cell>
                </list-row>
            </template>
            <template v-if="props.leadCollection.loading.value">
                <list-row v-for="i in selectedItemPerPage" :key="i">
                    <list-cell>
                        <bar-skeleton :width="100" />
                    </list-cell>
                    <list-cell>
                        <bar-skeleton :width="150" />
                    </list-cell>
                    <list-cell>
                        <bar-skeleton :width="100" />
                    </list-cell>
                    <list-cell>
                        <bar-skeleton :width="100" />
                    </list-cell>
                    <list-cell>
                        <bar-skeleton :width="100" />
                    </list-cell>
                </list-row>
            </template>

            <list-row v-if="props.leadCollection.data.value.length === 0 && !props.leadCollection.loading.value">
                <list-cell-text colspan="5">
                    <template #content>
                        We hebben geen contacten gevonden voor deze filters.
                    </template>
                </list-cell-text>
            </list-row>
        </template>
    </list-container>
</template>

<script setup lang="ts">
    import {
        BarSkeleton,
        ButtonDefault,
        ChipInformative,
        FlexContainer,
        IconSize,
        ListCell,
        ListCellHeader,
        ListCellText,
        ListContainer,
        ListRow,
        SvgIcon,
    } from '@keyvue/one-ui';
    import MoreVertIcon from '@keyvue/one-awesome-icons/icons/regular/chevron-right.svg?component';
    import type { LeadId, Lead, Address } from '@keyvue/one-api-client';
    import type { ReactiveCollection } from '@/services/keyvue-api/composables/useClient';
    import { ref } from 'vue';
    import { formatDate } from '@/shared/utils/date';
    import LeadListSortDecorator from '@/lead/lead/components/LeadListSortDecorator.vue';
    import { useRouter } from 'vue-router';

    export interface Props {
        selectedSort: { column: string, direction: 'ASC' | 'DESC' } | null
        selectedItemPerPage: number
        leadCollection: ReactiveCollection<Lead>
    }

    const props = defineProps<Props>();

    const router = useRouter();

    const emit = defineEmits<{
        (e: 'sort', value: { column: string, direction: 'ASC' | 'DESC' }): void;
    }>();

    const currentLeadId = ref<null | LeadId>(null);
    const currentThrottle = ref<undefined | number>(undefined);

    function setCurrentLeadId(id: LeadId) {
        clearTimeout(currentThrottle.value);
        currentThrottle.value = undefined;
        currentLeadId.value = id;
    }

    function resetCurrentLeadId() {
        currentThrottle.value = setTimeout(() => {
            currentLeadId.value = null;
        }, 50) as unknown as number;
    }

    function toDetail(lead: Lead) {
        router.push({
            name: 'contact-detail',
            params: {
                contactId: lead.primaryContact.id.value,
            },
        });
    }

    function hasAddress(address: Address): boolean {
        return address && address?.fullStreet !== '-' && address?.fullStreet !== '';
    }
</script>
